import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-leaf/50 focus:border-leaf";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setForm({
        phone: r.data.phone || "", email: r.data.email || "", hours: r.data.hours || "",
        address: r.data.address || "", service_area_note: r.data.service_area_note || "", about_text: r.data.about_text || "",
        pricing_enabled: !!r.data.pricing_enabled, pricing_items: r.data.pricing_items || [],
      }))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!form) return <Loader2 className="w-6 h-6 animate-spin text-leaf" />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const setItem = (i, k, v) => setForm((f) => ({ ...f, pricing_items: f.pricing_items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-settings-panel">
      <section className="rounded-2xl bg-white border border-forest/10 p-6 lg:p-8">
        <h2 className="font-display text-xl font-bold text-forest mb-5">Business Contact Info</h2>
        <p className="text-xs text-slate-500 mb-4">These appear on the public Contact page. Leave blank to hide.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Phone</label>
            <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="settings-phone-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Email</label>
            <input className={inputCls} value={form.email} onChange={set("email")} data-testid="settings-email-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Hours</label>
            <input className={inputCls} value={form.hours} onChange={set("hours")} placeholder="Mon-Sat, 8am-6pm" data-testid="settings-hours-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Address</label>
            <input className={inputCls} value={form.address} onChange={set("address")} placeholder="Street, City, State ZIP" data-testid="settings-address-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Service area note</label>
            <input className={inputCls} value={form.service_area_note} onChange={set("service_area_note")} data-testid="settings-area-note-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">About page text</label>
            <textarea rows={6} className={inputCls} value={form.about_text} onChange={set("about_text")} data-testid="settings-about-input" />
          </div>
        </div>
      </section>

      <section className="rounded-2xl bg-white border border-forest/10 p-6 lg:p-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-xl font-bold text-forest">Public Pricing Section</h2>
          <label className="flex items-center gap-2 text-sm font-semibold text-forest cursor-pointer">
            <input type="checkbox" checked={form.pricing_enabled} onChange={set("pricing_enabled")} className="w-4 h-4 accent-leaf" data-testid="pricing-enabled-toggle" />
            Show on website
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Hidden by default. Enable only once you've confirmed your rates - everything here displays publicly on the Home and service pages.
        </p>
        {form.pricing_items.map((item, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 mb-3" data-testid={`pricing-item-${i}`}>
            <input className={`${inputCls} sm:max-w-[200px]`} placeholder="Package name" value={item.name} onChange={(e) => setItem(i, "name", e.target.value)} data-testid={`pricing-name-${i}`} />
            <input className={`${inputCls} sm:max-w-[140px]`} placeholder="e.g. From $120" value={item.price} onChange={(e) => setItem(i, "price", e.target.value)} data-testid={`pricing-price-${i}`} />
            <input className={`${inputCls} flex-1 min-w-[200px]`} placeholder="What's included" value={item.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`pricing-desc-${i}`} />
            <button onClick={() => setForm((f) => ({ ...f, pricing_items: f.pricing_items.filter((_, idx) => idx !== i) }))} className="p-2.5 text-slate-400 hover:text-red-600" aria-label="Remove pricing item" data-testid={`pricing-delete-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button onClick={() => setForm((f) => ({ ...f, pricing_items: [...f.pricing_items, { name: "", price: "", description: "" }] }))} className="inline-flex items-center gap-2 rounded-full bg-leaf px-5 py-2.5 text-xs font-bold text-forest-deep hover:bg-mint" data-testid="pricing-add-button">
          <Plus className="w-4 h-4" /> Add Pricing Item
        </button>
      </section>

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 font-display font-bold text-white hover:bg-forest-soft disabled:opacity-60" data-testid="settings-save-button">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save All Settings
      </button>
    </div>
  );
}
